import thankYouImage from '../../assets/thank-you-icon.svg';
import bpkImage from '../../assets/billpaykiosk-logo.png';
import poweredByDtImage from '../../assets/powered-by-dt.png';
import style from './style.module.scss';
import { useStore } from '../../lib/hooks/useStore';
import { useEffect, useState, useRef } from 'react';
import GetCheckView from '../payment-check/components/GetCheckView';
import { observer } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import { PaymentType } from '../../lib/typegen/payment-type';

enum ViewState {
	ThankYou = 'ThankYou',
	GetCheck = 'GetCheck',
}

const ThankYou = observer(function () {
	const [isDoneClicked, setIsDoneClicked] = useState(false);
	const hasSetInitialView = useRef(false);

	const {
		uiStore: { setFooterEnabled, setBackEnabled, setShowCart, setTitle },
		sessionStore: { startEndSessionTasks, setSkipCountdownModal, setTimeoutNavigationCallback },
		accountStore,
		configStore: { getString },
	} = useStore();

	// Set initial view state only once per session, never overwrite ThankYou or GetCheck once set.
	// Manual and saved check details never put a physical check in the acceptor, so there is nothing to take back.
	useEffect(() => {
		if (!hasSetInitialView.current && accountStore.thankYouViewState == null) {
			if (
				accountStore.paymentType === PaymentType.Check &&
				!accountStore.preReceiptCheckRemovalPromptHandled &&
				!accountStore.isElectronicCheckPayment
			) {
				accountStore.setThankYouViewState(ViewState.GetCheck);
			} else {
				accountStore.setThankYouViewState(ViewState.ThankYou);
			}
			hasSetInitialView.current = true;
		}
	}, [accountStore, accountStore.paymentType, accountStore.preReceiptCheckRemovalPromptHandled, accountStore.isElectronicCheckPayment]);

	const renderGetCheckView = () => (
		<GetCheckView
			doneCallback={() => {
				if (isDoneClicked) return;
				setIsDoneClicked(true);
				accountStore.setThankYouViewState(ViewState.ThankYou);
			}}
		/>
	);

	const renderThankYouView = () => (
		<section className={style.section}>
			<img className={style.section__main} src={thankYouImage} alt='Thank You' />
			<img className={style.section__bpk} src={bpkImage} alt='BillPay Kiosk' />
			<img className={style.section__powered} src={poweredByDtImage} alt='Powered by DynaTouch Interactive Technologies' />
		</section>
	);

	// Suppress countdown modal and set auto-nav when showing GetCheckView
	useEffect(() => {
		if (accountStore.thankYouViewState === ViewState.GetCheck) {
			setSkipCountdownModal(true);
			setTimeoutNavigationCallback(() => {
				accountStore.setThankYouViewState(ViewState.ThankYou);
			});
			return () => {
				setSkipCountdownModal(false);
				setTimeoutNavigationCallback(null);
			};
		}
	}, [accountStore, setSkipCountdownModal, setTimeoutNavigationCallback]);

	// Call handleEndSession when ThankYou view is shown
	useEffect(() => {
		if (accountStore.thankYouViewState === ViewState.ThankYou) {
			const handleEndSession = () => {
				setTimeout(async () => {
					await startEndSessionTasks();
				}, 3000);
			};
			handleEndSession();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accountStore.thankYouViewState]);

	// Disable navigation and footer
	useEffect(() => {
		setBackEnabled(false);
		setShowCart(false);
		setFooterEnabled(false);
	}, [setBackEnabled, setFooterEnabled, setShowCart]);

	if (!accountStore.thankYouViewState) return null;

	switch (accountStore.thankYouViewState) {
		case ViewState.GetCheck:
			runInAction(() => setTitle(getString('check-returned-title')));
			return renderGetCheckView();
		default:
			runInAction(() => setTitle(getString('thank-you')));
			return renderThankYouView();
	}
});

export default ThankYou;
